import { useEffect, useId } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppLayout } from '../../components/templates/AppLayout/AppLayout';
import { Header } from '../../components/layout/Header';
import { Sidebar } from '../../components/Sidebar';
import { PageContent } from '../../components/templates/PageContent';
import { PageHeader } from '../../components/templates/PageHeader';
import { Section } from '../../components/templates/Section';
import { Card } from '../../components/atoms/Card';
import { Stack } from '../../components/atoms/Stack';
import { Inline } from '../../components/atoms/Inline';
import { Button } from '../../components/atoms/Button';
import { Icon } from '../../components/atoms/Icon/Icon';
import { Text } from '../../components/atoms/Typography';
import { Alert } from '../../components/atoms/Alert';
import { LiveRegion } from '../../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../../components/feedback/LoadErrorState/LoadErrorState';
import { useTranslation } from '../../i18n/useTranslation';
import { ZeovitModuleGate } from '../../components/zeovit/ZeovitModuleGate';
import { useZeovitRecommendation, useZeovitRecommendations } from '../../hooks/useZeovitRecommendations';
import type { ZeovitEffectClassification } from '../../services/zeovitRecommendationService';

/**
 * Stored effect classification (the values the backend persists on the checked
 * recommendation) → translation key + decorative icon. The text carries the meaning;
 * the icon is never the only signal (WCAG 1.4.1).
 */
const CLASSIFICATION_PRESENTATION: Record<ZeovitEffectClassification, { labelKey: string; icon: string }> = {
  darkening: { labelKey: 'zeovit.result.classification.darkening', icon: 'alert-triangle' },
  yellowing_paling: { labelKey: 'zeovit.result.classification.yellowingPaling', icon: 'alert-triangle' },
  browning: { labelKey: 'zeovit.result.classification.browning', icon: 'sun' },
  no_change: { labelKey: 'zeovit.result.classification.noChange', icon: 'minus' },
};

/** Navigation state set by the Periodic Check page: move focus to this page's heading. */
interface CheckResultLocationState {
  focusHeading?: boolean;
}

function CheckResultPageContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const headingId = useId();
  const { aquariumId, recommendationId } = useParams<{ aquariumId: string; recommendationId: string }>();

  const checked = useZeovitRecommendation(aquariumId, recommendationId);
  const lineage = useZeovitRecommendations(aquariumId);

  const isLoading = checked.isLoading || lineage.isLoading;
  const isError = checked.isError || lineage.isError;
  const recommendation = checked.data;
  // A check that was not an improvement creates the next iteration (pending, or capped) as a child.
  const next = lineage.data?.find((item) => item.parentRecommendationId === recommendationId) ?? null;
  const presentation = recommendation?.effectClassification
    ? CLASSIFICATION_PRESENTATION[recommendation.effectClassification]
    : null;
  const isPopulated = !isLoading && !isError && recommendation !== undefined;

  const focusHeading = Boolean((location.state as CheckResultLocationState | null)?.focusHeading);
  useEffect(() => {
    if (focusHeading && isPopulated) document.getElementById(headingId)?.focus();
  }, [focusHeading, isPopulated, headingId]);

  const aquariumPath = aquariumId ? `/aquariums/${encodeURIComponent(aquariumId)}` : '/aquariums';
  const recommendationPath = (id: string) => `${aquariumPath}/zeovit-recommendations/${encodeURIComponent(id)}`;

  const retry = () => {
    if (checked.isError) void checked.refetch();
    if (lineage.isError) void lineage.refetch();
  };

  let body;
  let announcement = '';
  if (isLoading) {
    announcement = t('zeovit.result.loading');
    body = <Text color="muted">{t('zeovit.result.loading')}</Text>;
  } else if (isError || !recommendation) {
    body = (
      <LoadErrorState
        title={t('zeovit.result.error.loadFailed')}
        onRetry={retry}
        retryLabel={t('zeovit.result.retry')}
        retrying={checked.isFetching || lineage.isFetching}
      />
    );
  } else if (!presentation) {
    // Not checked yet (e.g. the page was opened directly): offer the check instead.
    body = (
      <Alert
        tone="info"
        live="off"
        action={
          <Button
            variant="primary"
            onClick={() => navigate(`${recommendationPath(recommendation.id)}/check`)}
          >
            {t('zeovit.result.runCheck')}
          </Button>
        }
      >
        {t('zeovit.result.notChecked')}
      </Alert>
    );
  } else {
    announcement = t(presentation.labelKey);
    let nextText: string;
    let nextAction;
    if (!next) {
      nextText = t('zeovit.result.whatsNext.improved');
      nextAction = (
        <Button variant="primary" onClick={() => navigate(aquariumPath)}>
          {t('zeovit.result.done')}
        </Button>
      );
    } else if (next.status === 'capped') {
      nextText = t('zeovit.result.whatsNext.capped');
      nextAction = (
        <Button variant="primary" onClick={() => navigate(recommendationPath(next.id))}>
          {t('zeovit.result.viewManualReview')}
        </Button>
      );
    } else {
      nextText = t('zeovit.result.whatsNext.adjusted');
      nextAction = (
        <Button variant="primary" onClick={() => navigate(recommendationPath(next.id))}>
          {t('zeovit.result.viewNewRecommendation')}
        </Button>
      );
    }

    body = (
      <Stack gap="8">
        <Section title={t('zeovit.result.classificationSection')}>
          <Card>
            <Stack gap="3">
              <Inline gap="2" wrap>
                <Icon name={presentation.icon} />
                <Text weight="semibold">{t(presentation.labelKey)}</Text>
              </Inline>
              <Text color="muted">{t('zeovit.result.basis')}</Text>
            </Stack>
          </Card>
        </Section>
        <Section title={t('zeovit.result.whatsNextSection')}>
          <Card>
            <Stack gap="2">
              <Text>{nextText}</Text>
              <Inline>{nextAction}</Inline>
            </Stack>
          </Card>
        </Section>
      </Stack>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default">
          <PageHeader
            title={t('zeovit.result.title')}
            description={isPopulated && presentation ? t('zeovit.result.description') : undefined}
            headingProps={{ id: headingId, tabIndex: -1 }}
          />
          <LiveRegion visuallyHidden busy={isLoading}>
            {announcement}
          </LiveRegion>
          {body}
        </PageContent>
      }
    />
  );
}

/**
 * Only for aquariums whose supply system is ZeoVit (#1784): otherwise the page shows
 * the info notice instead (ZeovitModuleGate).
 */
export function CheckResultPage() {
  const { t } = useTranslation();
  const { aquariumId } = useParams<{ aquariumId: string }>();
  return (
    <ZeovitModuleGate aquariumId={aquariumId} title={t('zeovit.result.title')} width="default">
      <CheckResultPageContent />
    </ZeovitModuleGate>
  );
}

export default CheckResultPage;
